import * as React from 'react';
import { cn } from 'src/utils/utils';

import { Checkbox } from './checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuHeader,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './dropdown-menu';
import { Icon } from './icon';
import { Typography } from './typography';

/**
 * Dropdown option type for flexible dropdown items
 */
export type DropdownOption = {
  /** Unique identifier for the item */
  id?: string | number;
  /** Label text to display */
  label?: string;
  /** Icon to display (ReactNode or SVG component) */
  icon?: React.ReactNode | React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Click handler for the item */
  onClick?: () => void;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Whether to render a separator instead of an item */
  separator?: boolean;
  /** Whether the item is currently selected */
  selected?: boolean;
  /** Checked state when dropdown is in checkbox mode */
  checked?: boolean;
  /** Callback when checkbox checked state changes */
  onCheckedChange?: (checked: boolean) => void;
  /** Per-item custom render function - takes precedence over global renderItem */
  render?: () => React.ReactNode;
  /** Variant for styling (e.g. 'default', 'destructive') */
  variant?: 'default' | 'destructive';
  /** Additional className for the item */
  className?: string;
};

/**
 * Props for the Dropdown component
 */
export interface DropdownProps {
  /** Trigger element that opens the dropdown */
  trigger: React.ReactNode;
  /** Array of dropdown items */
  items: DropdownOption[];
  /** Sticky header rendered above the items */
  header?: React.ReactNode;
  /** Horizontal alignment of the dropdown content */
  align?: 'start' | 'center' | 'end';
  /** Side where the dropdown appears */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Additional className for the dropdown content */
  className?: string;
  /** Additional inline style for the dropdown content (e.g. to override stacking context) */
  style?: React.CSSProperties;
  /** Additional className for the trigger wrapper */
  triggerClassName?: string;
  /** Global render function for all items (overridden by item.render) */
  renderItem?: (item: DropdownOption) => React.ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Whether the dropdown is modal (blocks interaction with rest of page) */
  modal?: boolean;
  /** Side offset for the dropdown content */
  sideOffset?: number;
  disablePortal?: boolean;
  disableTrigger?: boolean;
  /** Whether all items render with a checkbox */
  checkbox?: boolean;
  /** Called when focus moves back to the trigger after close; call event.preventDefault() to keep focus from returning (useful to avoid stealing focus from an adjacent overlay opened on select) */
  onCloseAutoFocus?: (event: Event) => void;
}

/**
 * Helper to check if icon is an SVG component
 */
const isSvgComponent = (
  icon: React.ReactNode | React.ComponentType<React.SVGProps<SVGSVGElement>>
): icon is React.ComponentType<React.SVGProps<SVGSVGElement>> => {
  return typeof icon === 'function';
};

/**
 * Default item renderer - displays icon + label
 */
const DefaultItemContent: React.FC<{ item: DropdownOption }> = ({ item }) => (
  <>
    {item.icon ? (
      <span className="tw:inline-flex tw:shrink-0">
        {isSvgComponent(item.icon) ? (
          <Icon
            icon={item.icon}
            className="tw:text-icon-primary tw:group-hover:text-icon-primary-hover"
            sizeClass="tw:h-l tw:w-l"
          />
        ) : (
          item.icon
        )}
      </span>
    ) : null}
    {item.label ? (
      <Typography
        variant="h5"
        colorClass="tw:text-text-primary tw:group-hover:text-text-primary-hover"
      >
        {item.label}
      </Typography>
    ) : null}
  </>
);

/**
 * Flexible Dropdown Component
 *
 * A reusable dropdown component built on shadcn/ui that supports:
 * - Default rendering (icon + label)
 * - Global layout overrides using a renderItem prop
 * - Per-item overrides using an item.render function
 * - Full keyboard accessibility via Radix UI
 *
 * @example
 * // Basic usage with default rendering
 * <Dropdown
 *   trigger={<Button>Open Menu</Button>}
 *   items={[
 *     { id: 1, label: 'Edit', icon: EditIcon, onClick: handleEdit },
 *     { id: 2, label: 'Delete', icon: DeleteIcon, onClick: handleDelete },
 *   ]}
 * />
 *
 * @example
 * // With global renderItem override
 * <Dropdown
 *   trigger={<Button>Open Menu</Button>}
 *   items={items}
 *   renderItem={(item) => (
 *     <div className="flex items-center gap-4">
 *       <Avatar src={item.avatar} />
 *       <span>{item.label}</span>
 *     </div>
 *   )}
 * />
 *
 * @example
 * // With per-item render override
 * <Dropdown
 *   trigger={<Button>Open Menu</Button>}
 *   items={[
 *     { id: 1, label: 'Normal Item' },
 *     {
 *       id: 2,
 *       render: () => (
 *         <div className="flex justify-between w-full">
 *           <span>Dark Mode</span>
 *           <Switch checked={isDark} />
 *         </div>
 *       ),
 *       onClick: toggleDarkMode
 *     },
 *   ]}
 * />
 */
export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  header,
  align = 'start',
  side = 'bottom',
  className,
  style,
  triggerClassName,
  renderItem,
  open,
  onOpenChange,
  modal = true,
  disableTrigger = false,
  disablePortal = false,
  sideOffset = 4,
  checkbox = false,
  onCloseAutoFocus
}) => {
  const content = (
    <DropdownMenuContent
      align={align}
      side={side}
      sideOffset={sideOffset}
      onCloseAutoFocus={onCloseAutoFocus}
      className={cn('tw:min-w-[10rem]', className)}
      style={style}
    >
      {!!header && <DropdownMenuHeader>{header}</DropdownMenuHeader>}
      {items.map((item, index) => {
        if (item.separator) {
          return (
            <DropdownMenuSeparator key={item.id ?? `separator-${index}`} />
          );
        }

        // Resolve content: per-item render > global render > default
        // For checkbox items, never use asChild — we need to inject <Checkbox> ourselves
        const hasCustomContent = !!(item.render || renderItem) && !checkbox;

        const itemContent = item.render ? (
          item.render()
        ) : renderItem ? (
          renderItem(item)
        ) : (
          <DefaultItemContent item={item} />
        );

        return (
          <DropdownMenuItem
            key={item.id ?? index}
            id={item.id?.toString()}
            asChild={hasCustomContent}
            onClick={item.onClick}
            disabled={item.disabled}
            selected={item.selected}
            variant={item.variant}
            className={cn('tw:group', item.className)}
          >
            {checkbox ? (
              <>
                <Checkbox
                  checked={item.checked}
                  onCheckedChange={item.onCheckedChange}
                  disabled={item.disabled}
                  onClick={(e) => e.stopPropagation()}
                  className="tw:bg-primary"
                />
                {itemContent}
              </>
            ) : (
              itemContent
            )}
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuContent>
  );

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={modal}>
      <DropdownMenuTrigger
        disabled={disableTrigger}
        asChild
        className={triggerClassName}
      >
        {trigger}
      </DropdownMenuTrigger>

      {disablePortal ? (
        content
      ) : (
        <DropdownMenuPortal>{content}</DropdownMenuPortal>
      )}
    </DropdownMenu>
  );
};

Dropdown.displayName = 'Dropdown';

export default Dropdown;
